import { compose, css, space, variant } from '@wrisk/styled-system'
import { isNil, negate } from 'lodash-es'
import React, { FunctionComponent, useCallback } from 'react'
import styled from 'styled-components'

import { withDefaultProps } from '../../defaultProps'
import { Grid, GridProps } from '../layout'
import { TypoSizeProps, typoSizes } from '../typography'

export type ToggleVariants = 'default'

export interface ToggleProps
  extends TypoSizeProps,
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    > {
  variant?: ToggleVariants
  'data-testid'?: string | number | boolean
}

const Component: FunctionComponent<ToggleProps> = ({
  children,
  className,
  onChange,
  value,
  name,
  defaultChecked,
  'data-testid': dataTestId,
  ...props
}) => {
  const onInputChange = useCallback(
    (e) =>
      onChange?.({
        ...e,
        target: {
          ...e.target,
          value,
        },
        currentTarget: {
          ...e.currentTarget,
          value,
        },
      }),
    [onChange, value],
  )

  const id = props.id ?? [name, value].filter(negate(isNil)).join('-')

  return (
    <div className={className} data-testid={dataTestId}>
      <label>
        <input
          {...props}
          id={id}
          type='radio'
          name={name}
          value={value}
          onChange={onInputChange}
          autoComplete={props.autoComplete ?? 'off'}
          defaultChecked={defaultChecked ?? false}
        />
        <span>{children}</span>
      </label>
    </div>
  )
}

const defaultProps = withDefaultProps<ToggleProps>({
  variant: 'default',
  typoSize: 'sm',
})

export const Toggle = styled(Component).attrs(defaultProps)(
  css({
    position: 'relative',

    label: {
      transition: 'all 250ms ease',
      fontFamily: 'input',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      color: 'body',
      padding: 2,
      borderRadius: 1,

      '&:has(input:checked)': {
        fontWeight: 'bold',
        backgroundColor: 'chalk',
      },

      '&:has(input:not(:checked)):hover': {
        color: 'bodySecondary',
      },
    },

    input: {
      position: 'absolute',
      appearance: 'none',
      width: 0,
      height: 0,
      opacity: 0,
      zIndex: -1,
    },
  }),
  variant({
    scale: 'Toggle',
    variants: {
      default: {},
    },
  }),
  compose(typoSizes, space),
)

const GridComponent: FunctionComponent<GridProps & { columns: number }> = ({
  children,
  columns,
  ...props
}) => (
  <Grid
    gap='4px'
    gridAutoFlow='row'
    gridTemplateColumns={`repeat(${columns}, 1fr)`}
    {...props}
  >
    {children}
  </Grid>
)

export const ToggleGrid = styled(GridComponent)(
  css({
    padding: 2,
    backgroundColor: 'surfaceHighlight',
    borderRadius: 1,
  }),
  compose(space),
)
